import { UAContext, UserAgent } from '@quentin-sommer/react-useragent';
import Tippy from '@tippy.js/react';
import cn from 'classnames';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { TenancyOptionDateRangePicker } from '~components/datepicker';
import { TenancyOption } from '~components/datepicker/tenancy-option-helpers';
import Divider from '~components/divider';
import { ListGroup, ListItem } from '~components/list';
import { BaseModal } from '~components/modal';
import Typography from '~components/typography';
import {
  TenancyEndDateTypeOption,
  TenancyLengthOptions,
  TenancyLengthTypeOption,
  TenancyOptionRateTypeOption,
  TenancyStartDateTypeOption,
  TenancyTypeOption,
} from '~graphql/__generated__/globalTypes';
import { useTranslation } from '~i18n';
import { Check, More } from '~svg-components';
import styles from './tenancy-option-group.module.scss';

interface TenancyOptionsList {
  academicYear: { id: string; name: string };
  tenancyOptions: TenancyOption[];
}

interface ISelectTenancyTypes {
  tenancyOptionId: string;
  tenancyOptionStartDate?: Date;
  tenancyOptionEndDate?: Date;
  tenancyOptionRateType?: TenancyOptionRateTypeOption;
  academicYearId?: string;
  tenancyLengthOption?: TenancyLengthOptions;
}

interface ITenancyOptionGroupProps {
  tenancyOptionList: TenancyOptionsList[];
  onSelect: (params: ISelectTenancyTypes) => void;
  selectedValue: string;
  className?: string;
  setFlexibleMobileVisible?: (x: boolean) => void;
}

const TenancyOptionGroup: React.FC<ITenancyOptionGroupProps> = ({
  tenancyOptionList,
  onSelect,
  selectedValue,
  className,
  setFlexibleMobileVisible,
}) => {
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { t } = useTranslation(['student_portal']);
  const [isOpenId, setIsOpenId] = useState('');

  const getTenancyLengthTypeLable = (tenancyLengthType: TenancyLengthTypeOption | null) => {
    if (tenancyLengthType === TenancyLengthTypeOption.LE) {
      return t('max');
    }

    if (tenancyLengthType === TenancyLengthTypeOption.GE) {
      return t('min');
    }

    return;
  };

  const getFlexiableLabel = (tenancy: TenancyOption) => {
    if (tenancy.tenancyType === TenancyTypeOption.FLEXIBLE) {
      const flexibleTenancyOptionKeyParams = {
        tenancyLengthTypeLable: getTenancyLengthTypeLable(tenancy.tenancyLengthType),
        startDate: moment(tenancy.startDate).format('ddd, ll'),
        endDate: moment(tenancy.endDate).format('ddd, ll'),
        tenancyLength: tenancy.tenancyLength,
      };
      if (tenancy.startDateType === TenancyStartDateTypeOption.EQ) {
        return t(
          'select_bed.tenancy_option.flexible.starting_on_start_date',
          flexibleTenancyOptionKeyParams
        );
      }

      if (tenancy.endDateType === TenancyEndDateTypeOption.EQ) {
        return t(
          'select_bed.tenancy_option.flexible.ending_on_end_date',
          flexibleTenancyOptionKeyParams
        );
      }

      return t(
        'select_bed.tenancy_option.flexible.stay_between_start_date_and_end_date',
        flexibleTenancyOptionKeyParams
      );
    } else {
      return t('select_bed.tenancy_option.fixed', {
        startDate: moment(tenancy.startDate).format('ddd, ll'),
        endDate: moment(tenancy.endDate).format('ddd, ll'),
        tenancyLength: tenancy.tenancyLength,
      });
    }
  };

  const getDatePickerComponent = (tenancy: TenancyOption, academicYear: { id: string }) => {
    return (
      <div className="datepicker">
        <TenancyOptionDateRangePicker
          tenancyOption={tenancy}
          onFinishSelect={(data: { endDate?: Date; startDate?: Date }) => {
            if (data.endDate && data.startDate && tenancy.id && academicYear.id) {
              onSelect({
                tenancyOptionId: tenancy.id,
                tenancyOptionStartDate: data.startDate,
                tenancyOptionEndDate: data.endDate,
                academicYearId: academicYear.id,
                tenancyOptionRateType: tenancy.rateType,
                tenancyLengthOption: tenancy.tenancyLengthOption,
              });
            }
            if (isMobile) {
              setIsOpenId('');
            }
          }}
        />
        <style jsx>{`
          .datepicker {
            position: relative;
            display: flex;
            justify-content: center;
          }
        `}</style>
      </div>
    );
  };

  return (
    <ListGroup className={cn(styles.root, className)}>
      {tenancyOptionList.map(({ academicYear, tenancyOptions }) => {
        return (
          <div key={academicYear.id}>
            <div className={styles.academicYear}>
              <Typography variant="body3">{academicYear.name}</Typography>
            </div>
            {tenancyOptions &&
              tenancyOptions.map((tenancyOption, index) => {
                if (tenancyOption.tenancyType === 'FLEXIBLE') {
                  return (
                    <Tippy
                      key={tenancyOption.id?.toString()}
                      trigger="mouseenter focus click"
                      onShow={tip => {
                        tip.setProps({ trigger: 'mouseenter focus', hideOnClick: false });
                      }}
                      onHide={tip => {
                        tip.setProps({ trigger: 'mouseenter focus click', hideOnClick: false });
                      }}
                      enabled={!isMobile}
                      content={getDatePickerComponent(tenancyOption, academicYear)}
                      hideOnClick={true}
                      appendTo={e => e}
                      theme={'white'}
                      offset={'[0, 4]'}
                      placement={'left-start'}
                      popperOptions={{
                        modifiers: {
                          preventOverflow: {
                            boundariesElement: 'window',
                          },
                        },
                      }}
                    >
                      <div>
                        <UserAgent mobile>
                          {isOpenId === tenancyOption.id && (
                            <BaseModal
                              mobileHeightType="auto"
                              isOpen={isOpenId === tenancyOption.id}
                              onClose={() => {
                                setIsOpenId('');
                              }}
                            >
                              {getDatePickerComponent(tenancyOption, academicYear)}
                            </BaseModal>
                          )}
                        </UserAgent>
                        <div
                          onClick={event => {
                            event.nativeEvent.stopImmediatePropagation();
                            if (typeof setFlexibleMobileVisible === 'function') {
                              setFlexibleMobileVisible(false);
                            }

                            if (tenancyOption.id) {
                              setIsOpenId(tenancyOption.id);
                            }
                          }}
                          className={styles.item}
                        >
                          <div className={styles.tenancyOptionFlexible}>
                            <div className={styles.tenancyOption}>
                              <Typography className={styles.tenancyOptionName} variant="body3">
                                {tenancyOption.name}
                              </Typography>
                              <Typography
                                className="tenancy-option-select__sub-label"
                                color="var(--color-purple_3)"
                                variant="body3"
                              >
                                {getFlexiableLabel(tenancyOption)}
                              </Typography>
                            </div>
                            {tenancyOption.id === selectedValue && (
                              <Check width="27.8px" height="27.8px" />
                            )}
                            {tenancyOption.id !== selectedValue && (
                              <More width="14" height="8" className={styles.leftMore} />
                            )}
                          </div>
                          {index !== tenancyOptions?.length - 1 && <Divider />}
                        </div>
                      </div>
                    </Tippy>
                  );
                }
                // fixed tenancy option
                return (
                  <div key={tenancyOption.id?.toString()}>
                    <ListItem
                      onClick={e => {
                        if (tenancyOption.id) {
                          onSelect({
                            tenancyOptionId: tenancyOption.id,
                            tenancyOptionStartDate: moment(tenancyOption.startDate).toDate(),
                            tenancyOptionEndDate: moment(tenancyOption.endDate).toDate(),
                            tenancyOptionRateType: tenancyOption?.rateType,
                            academicYearId: academicYear.id,
                            tenancyLengthOption: tenancyOption.tenancyLengthOption,
                          });
                        }
                      }}
                      className={styles.listItem}
                      isSelected={selectedValue === tenancyOption.id}
                    >
                      <div className={styles.tenancyOption}>
                        <Typography className={styles.tenancyOptionName} variant="body3">
                          {tenancyOption.name}
                        </Typography>

                        <Typography
                          className="tenancy-option-select__sub-label"
                          color="var(--color-purple_3)"
                          variant="body3"
                        >
                          {tenancyOption?.tenancyLengthOption === TenancyLengthOptions.ROLLING
                            ? `${t('from')} ${moment(tenancyOption.startDate).format('ddd, ll')}`
                            : `${moment(tenancyOption.startDate).format('ddd, ll')} - ${moment(
                                tenancyOption.endDate
                              ).format('ddd, ll')}`}
                          {}
                        </Typography>
                      </div>
                    </ListItem>
                    {index !== tenancyOptions?.length - 1 && <Divider />}
                  </div>
                );
              })}
          </div>
        );
      })}
    </ListGroup>
  );
};
export default TenancyOptionGroup;
